import { useState, useEffect } from 'react'
import { getMonthStart, getCurrentDate } from '../services/date'
import logger from '../utils/logger'
import { authenticatedFetch } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import { useDateRangeWithURL } from './useDateRangeWithURL'

export const SEARCH_MODES = {
  CAMPAIGN_AND_DATE: 0,
  CALL_ID: 1,
  DATE_ONLY: 2,
  CONTENT: 3
}

/**
 * Custom hook to handle call search data and operations
 *
 * @returns {Object} State and methods for call search
 */
const useCallSearchData = () => {
  const [searchMode, setSearchMode] = useState(SEARCH_MODES.CAMPAIGN_AND_DATE)

  const {
    startDate,
    endDate,
    startDateString,
    endDateString,
    handleStartDateChange,
    handleEndDateChange
  } = useDateRangeWithURL(getMonthStart(), getCurrentDate())

  const [campaignName, setCampaignName] = useState('')
  const [callId, setCallId] = useState('')

  const [disposition, setDisposition] = useState([])
  const [direction, setDirection] = useState([])
  const [agentName, setAgentName] = useState([])
  const [durationMin, setDurationMin] = useState('')
  const [durationMax, setDurationMax] = useState('')

  const [filterOptions, setFilterOptions] = useState({
    dispositions: [],
    directions: [],
    agents: []
  })
  const [loadingFilters, setLoadingFilters] = useState(false)

  const [exporting, setExporting] = useState(false)

  const [formErrors, setFormErrors] = useState({
    campaignName: false,
    callId: false
  })

  const [campaigns, setCampaigns] = useState([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)

  const [calls, setCalls] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState(null)

  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCalls, setTotalCalls] = useState(0)
  const rowsPerPage = 25

  const [sortField, setSortField] = useState('call_date')
  const [sortDirection, setSortDirection] = useState('desc')

  const [contentSearchType, setContentSearchType] = useState('notes')
  const [searchQuery, setSearchQuery] = useState('')
  const [contentSearchError, setContentSearchError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true)

        const response = await authenticatedFetch(apiEndpoints.callsCampaigns, {
          signal: controller.signal
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch campaigns: ${response.status}`)
        }

        const data = await response.json()
        setCampaigns(data)
      } catch (err) {
        if (err.name !== 'AbortError') {
          logger.error('Error fetching campaigns for dropdown:', err)
        }
      } finally {
        clearTimeout(timeoutId)
        setLoadingCampaigns(false)
      }
    }

    fetchCampaigns()

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    const fetchFilterOptions = async () => {
      try {
        setLoadingFilters(true)
        const response = await authenticatedFetch(apiEndpoints.callsFilters, {
          signal: controller.signal
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch filter options: ${response.status}`)
        }
        const data = await response.json()
        setFilterOptions(data)
      } catch (err) {
        if (err.name !== 'AbortError') {
          logger.error('Error fetching filter options:', err)
        }
      } finally {
        clearTimeout(timeoutId)
        setLoadingFilters(false)
      }
    }

    fetchFilterOptions()

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [])

  useEffect(() => {
    if (searched && !loading) {
      setLoading(true)
      fetchCalls(1)
        .then(() => {
          setPage(1)
        })
        .catch((err) => {
          setError('Failed to sort calls')
          logger.error('Error sorting calls:', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [sortField, sortDirection])

  const handleSearchModeChange = (event, newValue) => {
    setSearchMode(newValue)
    setError(null)

    setFormErrors({
      campaignName: false,
      callId: false
    })

    setSearched(false)
    setCalls([])

    setPage(1)

    setDisposition([])
    setDirection([])
    setAgentName([])
    setContentSearchType('notes')
    setSearchQuery('')
    setContentSearchError('')
  }

  const buildFilterParams = (pageNum = 1) => {
    const params = new URLSearchParams()

    switch (searchMode) {
      case SEARCH_MODES.CAMPAIGN_AND_DATE:
        if (campaignName) {
          params.append('campaignName', campaignName)
        }
        if (startDateString) {
          params.append('startDate', startDateString)
        }
        if (endDateString) {
          params.append('endDate', endDateString)
        }
        break
      case SEARCH_MODES.CALL_ID:
        if (callId) {
          params.append('callId', callId)
        }
        break
      case SEARCH_MODES.DATE_ONLY:
        if (startDateString) {
          params.append('startDate', startDateString)
        }
        if (endDateString) {
          params.append('endDate', endDateString)
        }
        break
      default:
        break
    }

    if (searchMode === SEARCH_MODES.CONTENT) {
      const safeType = { notes: 'call_notes', caller: 'caller_name' }[contentSearchType] || 'call_notes'
      params.append('contentSearchType', safeType)
      params.append('searchQuery', searchQuery.trim())
    }

    if (disposition.length) params.append('disposition', disposition.join(','))
    if (direction.length) params.append('direction', direction.join(','))
    if (agentName.length) params.append('agentName', agentName.join(','))
    if (durationMin) params.append('durationMin', durationMin)
    if (durationMax) params.append('durationMax', durationMax)

    params.append('sortField', sortField)
    params.append('sortDirection', sortDirection)

    params.append('page', pageNum)
    params.append('limit', rowsPerPage)

    return params
  }

  const fetchCalls = async (pageNum) => {
    try {
      const params = buildFilterParams(pageNum)

      const url = `${apiEndpoints.calls}?${params.toString()}`
      const response = await authenticatedFetch(url)

      if (!response.ok) {
        throw new Error(`Failed to fetch calls: ${response.status}`)
      }

      const data = await response.json()
      setCalls(data.calls || [])
      setTotalCalls(data.total || 0)
      setTotalPages(data.totalPages || 1)

      return data
    } catch (error) {
      logger.error('Fetch calls error:', error)

      throw error
    }
  }

  const handleSearch = async (t) => {
    if (loading) return

    setError(null)

    const errors = {
      campaignName: false,
      callId: false
    }

    switch (searchMode) {
      case SEARCH_MODES.CAMPAIGN_AND_DATE:
      case SEARCH_MODES.DATE_ONLY:
        break
      case SEARCH_MODES.CALL_ID:
        errors.callId = !callId
        break
      case SEARCH_MODES.CONTENT:
        if (!campaignName || !searchQuery.trim()) {
          setContentSearchError('Campaign and search query are required for content search.')
          return
        }
        setContentSearchError('')
        break
      default:
        break
    }

    setFormErrors(errors)

    if (searchMode === SEARCH_MODES.CALL_ID && errors.callId) {
      setError(t('errors.formValidation'))
      return
    }

    setLoading(true)
    setPage(1)

    Promise.resolve()
      .then(() => {
        return fetchCalls(1)
          .then(data => {
            setSearched(true)
            return data
          })
          .catch(err => {
            logger.error('Error searching calls:', err)
            setError(err.message || 'Failed to search calls')
            setCalls([])
            setSearched(false)
          })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleSort = (field) => {
    if (loading) return

    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const getSortDirectionIcon = (field) => {
    if (field !== sortField) return null
    return sortDirection === 'asc' ? 'sort-up' : 'sort-down'
  }

  const resetSorting = () => {
    if (loading) return
    setSortField('call_date')
    setSortDirection('desc')
  }

  const handleExportCsv = async () => {
    if (exporting) return
    try {
      setExporting(true)
      const params = buildFilterParams(1)
      params.delete('page')
      params.delete('limit')
      const url = `${apiEndpoints.callsExportCsv}?${params.toString()}`
      const response = await authenticatedFetch(url)

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`)
      }

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const filename = `calls_export_${new Date().toISOString().slice(0, 10)}.csv`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(link)
    } catch (err) {
      logger.error('CSV export error:', err)
      setError('Failed to export CSV. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const handlePageChange = (event, value) => {
    if (loading) return

    setPage(value)
    setLoading(true)

    fetchCalls(value)
      .then(() => {
        setLoading(false)
        setSearched(true)
      })
      .catch((err) => {
        setLoading(false)
        setError('Failed to fetch calls')
        logger.error('Error fetching calls:', err)
      })
  }

  return {
    searchMode,
    campaignName,
    callId,
    startDate,
    endDate,
    formErrors,

    campaigns,
    loadingCampaigns,

    calls,
    loading,
    searched,
    error,

    page,
    totalPages,
    totalCalls,
    rowsPerPage,

    sortField,
    sortDirection,

    setSearchMode,
    setCampaignName,
    setCallId,
    setStartDate: handleStartDateChange,
    setEndDate: handleEndDateChange,
    setFormErrors,

    handleSearchModeChange,
    handleSearch,
    handleSort,
    resetSorting,
    getSortDirectionIcon,
    handlePageChange,
    fetchCalls,

    contentSearchType,
    searchQuery,
    contentSearchError,

    setContentSearchType,
    setSearchQuery,
    setContentSearchError,

    disposition,
    direction,
    agentName,
    durationMin,
    durationMax,
    setDisposition,
    setDirection,
    setAgentName,
    setDurationMin,
    setDurationMax,

    filterOptions,
    loadingFilters,

    exporting,
    handleExportCsv
  }
}

export default useCallSearchData
